import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { RadioButton } from "primereact/radiobutton";
import { Checkbox } from "primereact/checkbox";
import Gallery from "./Gallery";
import axios from "axios";

interface Ad {
  id: string;
  title: string;
  text: string;
  imageUrl?: string;
  galleryImages: string[];
  youtubeUrl?: string;
  location: "Lebanon" | "Abidjan";
  createdAt: number;
  type: "ad";
  showInFlipcard: boolean;
}

const EditAds: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = React.useRef<Toast>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showInFlipcard, setShowInFlipcard] = useState(false);
  const [ad, setAd] = useState<Ad>({
    id: "",
    title: "",
    text: "",
    imageUrl: "",
    galleryImages: [],
    youtubeUrl: "",
    location: "Lebanon",
    createdAt: Date.now(),
    type: "ad",
    showInFlipcard: false,
  });

  useEffect(() => {
    const fetchAd = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const response = await axios.get(
          process.env.REACT_APP_API + `/ad-spaces/${id}`
        );
        const adData = response.data as Ad;
        setAd(adData);
        setShowInFlipcard(adData.showInFlipcard || false);
      } catch (error) {
        console.error("Error fetching ad:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load ad",
          life: 3000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAd();
  }, [id, navigate]);

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        setUploading(true);
        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload_preset", "ml_default");
        formData.append("cloud_name", "dzuppkxqa");

        const response = await fetch(
          `https://api.cloudinary.com/v1_1/dzuppkxqa/image/upload`,
          {
            method: "POST",
            body: formData,
          }
        );

        const data = await response.json();
        setAd((prev) => ({ ...prev, imageUrl: data.secure_url }));
      } catch (error) {
        console.error("Error uploading image:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to upload image",
          life: 3000,
        });
      } finally {
        setUploading(false);
      }
    }
  };

  const handleGalleryImagesChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (files) {
      try {
        setUploading(true);
        const uploadPromises = Array.from(files).map(async (file) => {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("upload_preset", "ml_default");
          formData.append("cloud_name", "dzuppkxqa");

          const response = await fetch(
            `https://api.cloudinary.com/v1_1/dzuppkxqa/image/upload`,
            {
              method: "POST",
              body: formData,
            }
          );

          const data = await response.json();
          return data.secure_url;
        });

        const newImageUrls = await Promise.all(uploadPromises);
        setAd((prev) => ({
          ...prev,
          galleryImages: [...prev.galleryImages, ...newImageUrls],
        }));
      } catch (error) {
        console.error("Error uploading gallery images:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to upload gallery images",
          life: 3000,
        });
      } finally {
        setUploading(false);
      }
    }
  };

  const removeGalleryImage = (index: number) => {
    setAd((prev) => ({
      ...prev,
      galleryImages: prev.galleryImages.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    try {
      setLoading(true);

      // If enabling flipcard, check the limit
      if (showInFlipcard) {
        const response = await axios.get(
          `${process.env.REACT_APP_API}/ad-spaces?showInFlipcard=true`
        );
        const currentFlipCards = response.data.filter(
          (adItem: any) => adItem.id !== id
        );

        if (currentFlipCards.length >= 5) {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail:
              "Maximum limit of 5 flipcards reached. Please uncheck some posts first.",
            life: 5000,
          });
          setShowInFlipcard(false);
          return;
        }
      }
      const updatedAd = {
        ...ad,
        showInFlipcard,
        updatedAt: Date.now(),
      };

      await axios.put(
        process.env.REACT_APP_API + `/ad-spaces/${id}`,
        updatedAd
      );

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Ad updated successfully",
        life: 3000,
      });

      navigate(ad.location === "Lebanon" ? "/lebanon-ads" : "/abidjan-ads");
    } catch (error) {
      console.error("Error updating ad:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to update ad",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <i className="pi pi-spin pi-spinner text-4xl"></i>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <Toast ref={toast} />
      <Card className="mb-4">
        <form onSubmit={handleSubmit} className="flex flex-column gap-4">
          <div className="flex flex-column gap-2">
            <label htmlFor="title">Title</label>
            <InputText
              id="title"
              value={ad.title}
              onChange={(e) =>
                setAd((prev) => ({ ...prev, title: e.target.value }))
              }
              required
            />
          </div>

          <div className="flex flex-column gap-2">
            <label htmlFor="text">Content</label>
            <InputTextarea
              id="text"
              value={ad.text}
              onChange={(e) =>
                setAd((prev) => ({ ...prev, text: e.target.value }))
              }
              rows={5}
              required
              className="whitespace-pre-wrap w-full"
            />
          </div>

          <div className="flex flex-column gap-2">
            <label>Location</label>
            <div className="p-d-flex p-ai-center">
              <RadioButton
                inputId="Lebanon"
                name="location"
                value="Lebanon"
                onChange={(e) =>
                  setAd((prev) => ({ ...prev, location: e.value }))
                }
                checked={ad.location === "Lebanon"}
              />
              <label htmlFor="Lebanon" className="p-ml-2">
                Lebanon
              </label>

              <RadioButton
                inputId="Abidjan"
                name="location"
                value="Abidjan"
                onChange={(e) =>
                  setAd((prev) => ({ ...prev, location: e.value }))
                }
                checked={ad.location === "Abidjan"}
                className="p-ml-4"
              />
              <label htmlFor="Abidjan" className="p-ml-2">
                Abidjan
              </label>
            </div>
          </div>

          <div
            className="p-field-checkbox"
            style={{ marginTop: "1rem", marginBottom: "1rem" }}
          >
            <Checkbox
              inputId="showInFlipcard"
              checked={showInFlipcard}
              onChange={(e) => setShowInFlipcard(e.target.checked ?? false)}
            />
            <label
              htmlFor="showInFlipcard"
              className="p-checkbox-label"
              style={{ marginLeft: "0.5rem" }}
            >
              Show in Home Flipcard Section
            </label>
          </div>

          <div className="flex flex-column gap-2">
            <label htmlFor="mainImage">Main Image</label>
            <div className="flex align-items-center gap-2">
              <Button
                type="button"
                icon="pi pi-upload"
                className="p-button-outlined"
                onClick={() => document.getElementById("mainImage")?.click()}
                loading={uploading}
              />
              <input
                type="file"
                id="mainImage"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleImageChange}
              />
              {ad.imageUrl && (
                <img
                  src={ad.imageUrl}
                  alt="Main"
                  style={{ height: "50px", objectFit: "cover" }}
                />
              )}
            </div>
          </div>

          <div className="flex flex-column gap-2">
            <label htmlFor="galleryImages">Gallery Images</label>
            <div className="flex align-items-center gap-2">
              <Button
                type="button"
                icon="pi pi-images"
                className="p-button-outlined"
                onClick={() =>
                  document.getElementById("galleryImages")?.click()
                }
                loading={uploading}
              />
              <input
                type="file"
                id="galleryImages"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={handleGalleryImagesChange}
              />
            </div>
            {ad.galleryImages.length > 0 && (
              <Gallery
                images={ad.galleryImages}
                isEditing={true}
                onImageRemove={removeGalleryImage}
              />
            )}
          </div>

          <div className="flex flex-column gap-2">
            <label htmlFor="youtubeUrl">YouTube URL (Optional)</label>
            <InputText
              id="youtubeUrl"
              value={ad.youtubeUrl}
              onChange={(e) =>
                setAd((prev) => ({ ...prev, youtubeUrl: e.target.value }))
              }
              placeholder="https://www.youtube.com/watch?v=..."
            />
          </div>

          <Button
            type="submit"
            label="Update Ad"
            icon="pi pi-check"
            className="mt-4"
          />
        </form>
      </Card>
    </div>
  );
};

export default EditAds;
